import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Copy, Landmark, Smartphone, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESUME_KEY = "miro_one_partner_draft";
const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function PartnerPaymentSubmit() {
  const { appId } = useParams();
  const [token, setToken] = useState(null);
  const [app, setApp] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    amount_submitted: "",
    payment_method: "upi",
    utr: "",
    sender_account_holder: "",
    payment_date: new Date().toISOString().slice(0, 10),
    applicant_notes: "",
  });
  const [proof, setProof] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Try to recover resume_token from localStorage; the wizard writes it
      // when the applicant submits, and the /payment/:id URL is reached from
      // the wizard's SubmittedScreen or a re-visit within the same device.
      const cached = safeParse(localStorage.getItem(RESUME_KEY)) || safeParse(localStorage.getItem(`miro_one_partner_submitted_${appId}`));
      const tok = cached?.token;
      if (!tok) { setLoading(false); return; }
      setToken(tok);
      const [a, s] = await Promise.all([
        axios.get(`${API}/public/partner-applications/${appId}`, { params: { resume_token: tok } }),
        axios.get(`${API}/public/payment-settings`),
      ]);
      setApp(a.data);
      setSettings(s.data);
      setForm((f) => ({ ...f,
        amount_submitted: a.data.fee_snapshot?.total || "",
      }));
    } catch (e) {
      toast.error("Unable to load your application. Please try the link in your confirmation email.");
    } finally { setLoading(false); }
  }, [appId]);

  useEffect(() => { load(); }, [load]);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); return; }
    const fd = new FormData();
    fd.append("kind", "payment_proof");
    fd.append("resume_token", token);
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/public/partner-applications/${appId}/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setProof(r.data);
      toast.success(`Uploaded ${r.data.original_filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const submit = async () => {
    if (!form.utr) { toast.error("UTR is required"); return; }
    if (!form.amount_submitted) { toast.error("Amount is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount_submitted: Number(form.amount_submitted),
        payment_proof_doc_id: proof?.id || null,
      };
      const r = await axios.post(
        `${API}/public/partner-applications/${appId}/payment`,
        payload, { params: { resume_token: token } }
      );
      setSubmitted(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSaving(false); }
  };

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  const [payingOnline, setPayingOnline] = useState(false);
  const [onlinePaid, setOnlinePaid] = useState(null);

  const payOnline = async () => {
    setPayingOnline(true);
    try {
      // Wait up to ~4s for checkout.razorpay.com/v1/checkout.js to load.
      for (let i = 0; i < 20 && !window.Razorpay; i++) {
        await new Promise(r => setTimeout(r, 200));
      }
      if (!window.Razorpay) {
        setPayingOnline(false);
        toast.error("Payment gateway didn't load", {
          description: "Please check your internet connection or disable ad-blockers, then reload the page.",
        });
        return;
      }
      const r = await axios.post(
        `${API}/public/partner-applications/${appId}/razorpay/create-order`,
        null, { params: { resume_token: token } });
      const { order_id, amount, currency, key_id, reference,
              applicant_name, applicant_email, applicant_phone } = r.data;
      const options = {
        key: key_id,
        amount,
        currency,
        order_id,
        name: "MIRO Consultancy",
        description: `Partner onboarding — ${reference}`,
        image: "/miro-logo.png",
        theme: { color: "#041B46" },
        prefill: {
          name: applicant_name || "",
          email: applicant_email || "",
          contact: applicant_phone || "",
        },
        modal: { ondismiss: () => { setPayingOnline(false); toast.message("Checkout cancelled"); } },
        handler: async (resp) => {
          try {
            const v = await axios.post(
              `${API}/public/partner-applications/${appId}/razorpay/verify-payment`,
              { razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature },
              { params: { resume_token: token } });
            setOnlinePaid(v.data);
            setPayingOnline(false);
          } catch (err) {
            setPayingOnline(false);
            toast.error(err?.response?.data?.detail || "Signature verification failed");
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (e) => {
        setPayingOnline(false);
        toast.error(e?.error?.description || "Payment failed", {
          description: e?.error?.reason || "Please retry or use manual payment below",
        });
      });
      rzp.open();
    } catch (e) {
      setPayingOnline(false);
      toast.error(e?.response?.data?.detail || "Unable to start online payment");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[var(--miro-text-muted)]">Loading…</div>;
  }
  if (!app || !token) {
    return <MissingTokenScreen/>;
  }
  if (submitted) return <SubmittedScreen appId={appId} data={submitted} app={app}/>;
  if (onlinePaid) return <OnlinePaidScreen app={app} data={onlinePaid}/>;

  const fee = app.fee_snapshot || {};
  const isFranch = app.partner_type === "franchise";

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: "var(--miro-bg, #F5F7FB)" }}>
      <div className="max-w-3xl mx-auto" data-testid="partner-payment-page">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]">
          <ArrowLeft size={13}/> Back to sign in
        </Link>
        <div className="mt-4 mb-6">
          <div className="miro-overline">Complete your payment</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-[var(--miro-navy)] tracking-tight">
            Payment for {app.first_name} {app.last_name}
          </h1>
          <div className="mt-2 flex items-center gap-3 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-[var(--miro-navy)] text-white font-semibold font-mono" data-testid="payment-ref">
              {app.payment_reference}
            </span>
            <span className="text-[var(--miro-text-muted)]">Please quote this reference on your UPI or bank transfer.</span>
          </div>
        </div>

        {/* Payment target */}
        <div className="bg-white border border-[var(--miro-border)] rounded-2xl p-5 sm:p-6 mb-6" data-testid="payment-target">
          <div className="miro-overline">Pay to</div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TargetCell icon={<Smartphone size={14}/>} label="UPI ID"
              value={settings?.upi_id} testid="target-upi"
              onCopy={() => copy(settings?.upi_id, "UPI ID")}/>
            <TargetCell icon={<Landmark size={14}/>} label="Legal business name" value={settings?.legal_business_name}/>
            <TargetCell label="Bank account" value={settings?.bank_account_name}/>
            {settings?.bank_name && <TargetCell label="Bank" value={settings.bank_name}/>}
            {settings?.account_number && (
              <TargetCell label="Account number" value={settings.account_number}
                onCopy={() => copy(settings.account_number, "Account number")}/>
            )}
            {settings?.ifsc && <TargetCell label="IFSC" value={settings.ifsc} onCopy={() => copy(settings.ifsc, "IFSC")}/>}
            {settings?.branch && <TargetCell label="Branch" value={settings.branch}/>}
            {settings?.gstin && <TargetCell label="GSTIN" value={settings.gstin}/>}
          </div>
          {settings?.payment_instructions && (
            <div className="mt-4 text-xs text-[var(--miro-text-secondary)] bg-[var(--miro-bg)] p-3 rounded-lg">
              {settings.payment_instructions}
            </div>
          )}

          <div className="mt-4 flex justify-between items-center bg-[var(--miro-bg)] p-3 rounded-lg">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[var(--miro-text-muted)]">Amount payable</div>
              <div className="font-display font-bold text-2xl text-[var(--miro-navy)]" data-testid="amount-payable">{inr(fee.total)}</div>
            </div>
            {isFranch && (
              <div className="text-right text-[11px] text-[var(--miro-text-muted)] leading-relaxed">
                Franchise fee {inr(fee.franchise_fee)} + GST {inr(fee.franchise_gst)}<br/>
                + Annual membership {inr(fee.membership_fee)}
              </div>
            )}
          </div>

          {/* Online payment */}
          <div className="mt-4 border border-[var(--miro-border)] rounded-xl p-4 bg-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="font-semibold text-[var(--miro-navy)]">Pay online instantly</div>
                <div className="text-xs text-[var(--miro-text-muted)] mt-0.5">
                  UPI, Cards, NetBanking &amp; Wallets via Razorpay. Verified automatically — no UTR needed.
                </div>
              </div>
              <Button onClick={payOnline} disabled={payingOnline}
                      className="bg-[var(--miro-navy)] hover:opacity-90 text-white shrink-0"
                      data-testid="pay-online-btn">
                {payingOnline ? "Opening…" : `Pay ${inr(fee.total)} online`}
              </Button>
            </div>
          </div>

          <div className="mt-3 text-center text-[11px] text-[var(--miro-text-muted)]">
            — or pay manually via UPI / bank transfer and submit details below —
          </div>
        </div>

        {/* Payment submission form */}
        <div className="bg-white border border-[var(--miro-border)] rounded-2xl p-5 sm:p-6">
          <div className="miro-overline">After paying — share your details</div>
          <h2 className="font-display font-bold text-xl text-[var(--miro-navy)] mt-1">Payment details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            <div>
              <Label>Payment method *</Label>
              <Select value={form.payment_method} onValueChange={upd("payment_method")}>
                <SelectTrigger data-testid="payment-method" className="mt-1 h-11 bg-white"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="neft">NEFT</SelectItem>
                  <SelectItem value="imps">IMPS</SelectItem>
                  <SelectItem value="rtgs">RTGS</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>UTR / transaction reference *</Label>
              <Input data-testid="payment-utr" value={form.utr} onChange={upd("utr")} className="mt-1 h-11 bg-white" required/>
            </div>
            <div>
              <Label>Amount paid (₹) *</Label>
              <Input data-testid="payment-amount" type="number" min={0} step="0.01"
                     value={form.amount_submitted} onChange={upd("amount_submitted")}
                     className="mt-1 h-11 bg-white" required/>
            </div>
            <div>
              <Label>Payment date *</Label>
              <Input data-testid="payment-date" type="date"
                     value={form.payment_date} onChange={upd("payment_date")}
                     className="mt-1 h-11 bg-white" required/>
            </div>
            <div className="sm:col-span-2">
              <Label>Sender account holder name</Label>
              <Input data-testid="payment-sender" value={form.sender_account_holder}
                     onChange={upd("sender_account_holder")} className="mt-1 h-11 bg-white"/>
            </div>
            <div className="sm:col-span-2">
              <Label>Notes for the MIRO finance team (optional)</Label>
              <Textarea data-testid="payment-notes" rows={2} value={form.applicant_notes}
                        onChange={upd("applicant_notes")} className="mt-1"/>
            </div>
          </div>

          <div className="mt-4">
            <Label>Payment proof (screenshot / receipt PDF)</Label>
            <ProofUpload proof={proof} onFile={upload}/>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div className="text-[11px] text-[var(--miro-text-muted)]">
              Your submission is manually verified by the MIRO finance team.
            </div>
            <Button data-testid="payment-submit-btn" onClick={submit} disabled={saving}
                    className="h-11 gap-1.5" style={{ background: "var(--miro-navy)" }}>
              {saving ? "Submitting…" : "Submit for verification"} <Check size={14}/>
            </Button>
          </div>
        </div>

        {isFranch && (
          <FranchiseConsult appId={appId} token={token}/>
        )}
      </div>
    </div>
  );
}

function safeParse(s) { try { return s ? JSON.parse(s) : null; } catch { return null; } }

function TargetCell({ icon, label, value, testid, onCopy }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      {icon && <span className="mt-1 text-[var(--miro-blue)]">{icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-widest text-[var(--miro-text-muted)]">{label}</div>
        <div className="text-sm font-mono text-[var(--miro-navy)] break-all" data-testid={testid}>{value}</div>
      </div>
      {onCopy && (
        <button onClick={onCopy} data-testid={testid ? `${testid}-copy` : undefined}
                className="text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]">
          <Copy size={14}/>
        </button>
      )}
    </div>
  );
}

function ProofUpload({ proof, onFile }) {
  const ref = React.useRef(null);
  return (
    <div className="mt-1 flex items-center gap-3 border border-[var(--miro-border)] rounded-lg bg-white p-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[var(--miro-navy)]">Upload proof (JPG / PNG / PDF, max 10 MB)</div>
        <div className="text-[11px] text-[var(--miro-text-muted)] truncate">
          {proof ? `Uploaded: ${proof.original_filename}` : "Optional but strongly recommended"}
        </div>
      </div>
      <input ref={ref} type="file" hidden accept="image/*,application/pdf"
             onChange={(e) => onFile(e.target.files?.[0])}
             data-testid="payment-proof-input"/>
      <Button variant="outline" size="sm" onClick={() => ref.current?.click()}
              data-testid="payment-proof-btn" className="h-9 gap-1.5">
        <Upload size={13}/> {proof ? "Replace" : "Upload"}
      </Button>
    </div>
  );
}

function FranchiseConsult({ appId, token }) {
  const [form, setForm] = useState({ preferred_date: "", preferred_time: "", preferred_language: "", note: "" });
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/public/partner-applications/${appId}/consult`, form,
        { params: { resume_token: token } });
      setSent(true);
      toast.success("Our franchise team will reach out on your preferred slot.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Callback request failed");
    } finally { setSaving(false); }
  };
  if (sent) {
    return (
      <div className="mt-6 bg-white border border-[var(--miro-border)] rounded-2xl p-6 text-center"
           data-testid="franchise-consult-sent">
        <div className="miro-overline">Franchise consultation booked</div>
        <p className="text-sm text-[var(--miro-text-secondary)] mt-2">
          Our franchise team has been notified and will contact you shortly.
          Meanwhile, you can complete your payment above whenever you're ready.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-6 bg-white border border-[var(--miro-border)] rounded-2xl p-5 sm:p-6"
         data-testid="franchise-consult-block">
      <div className="miro-overline">Not ready to pay?</div>
      <h3 className="font-display font-bold text-xl text-[var(--miro-navy)] mt-1">Consult MIRO team first</h3>
      <p className="text-sm text-[var(--miro-text-secondary)] mt-2">
        Prefer to speak with our franchise team before paying? Book a callback and we'll
        assign a MIRO representative to walk you through franchise benefits and terms.
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label>Preferred date</Label>
          <Input data-testid="consult-date" type="date" value={form.preferred_date} onChange={upd("preferred_date")} className="mt-1 h-11 bg-white"/>
        </div>
        <div>
          <Label>Preferred time</Label>
          <Input data-testid="consult-time" type="time" value={form.preferred_time} onChange={upd("preferred_time")} className="mt-1 h-11 bg-white"/>
        </div>
        <div>
          <Label>Preferred language</Label>
          <Input data-testid="consult-language" value={form.preferred_language} onChange={upd("preferred_language")} className="mt-1 h-11 bg-white" placeholder="English / Hindi …"/>
        </div>
      </div>
      <div className="mt-3">
        <Label>Anything you'd like to discuss?</Label>
        <Textarea data-testid="consult-note" rows={2} value={form.note} onChange={upd("note")} className="mt-1"/>
      </div>
      <div className="mt-4 flex justify-end">
        <Button data-testid="consult-submit-btn" onClick={submit} disabled={saving}
                variant="outline" className="h-10">Request callback</Button>
      </div>
    </div>
  );
}

function MissingTokenScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md bg-white border border-[var(--miro-border)] rounded-2xl p-8 text-center">
        <div className="miro-overline">Payment session unavailable</div>
        <p className="text-sm text-[var(--miro-text-secondary)] mt-3">
          We couldn't find your application session on this device. Please open the payment link
          from your confirmation email, or contact <a href="mailto:support@miroconsultancy.co.in" className="text-[var(--miro-blue)] underline">support@miroconsultancy.co.in</a>.
        </p>
        <Button asChild className="w-full mt-6 h-11" style={{ background: "var(--miro-navy)" }}>
          <Link to="/login">Back to sign in</Link>
        </Button>
      </div>
    </div>
  );
}

function SubmittedScreen({ appId, data, app }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md bg-white border border-[var(--miro-border)] rounded-2xl p-8"
           data-testid="payment-submitted">
        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
             style={{ background: "var(--miro-green, #12B981)" }}>
          <Check className="text-white" size={26}/>
        </div>
        <div className="miro-overline text-center mt-6">Payment submitted</div>
        <h1 className="font-display font-black text-3xl text-center text-[var(--miro-navy)] mt-1">
          Verification in progress
        </h1>
        <p className="text-sm text-[var(--miro-text-secondary)] mt-4 text-center">
          We've received your payment details for <strong>{data.reference}</strong>. Our finance
          team manually verifies every submission and will notify you by email as soon as your
          account is activated.
        </p>
        <div className="mt-5 rounded-lg border border-[var(--miro-border)] bg-[var(--miro-bg)] p-4 text-sm text-[var(--miro-text-primary)]">
          <div className="flex justify-between"><span>Amount submitted</span><strong>₹{Number(data.amount_submitted).toLocaleString("en-IN")}</strong></div>
          <div className="flex justify-between"><span>UTR</span><strong className="font-mono text-xs">{data.utr}</strong></div>
        </div>
        <Button asChild className="w-full mt-6 h-11 font-semibold" style={{ background: "var(--miro-navy)" }}>
          <Link to="/login">Back to sign in</Link>
        </Button>
      </div>
    </div>
  );
}


function OnlinePaidScreen({ app, data }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md bg-white border border-[var(--miro-border)] rounded-2xl p-8"
           data-testid="payment-online-success">
        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
             style={{ background: "var(--miro-green, #12B981)" }}>
          <Check className="text-white" size={26}/>
        </div>
        <div className="miro-overline text-center mt-6">Payment verified</div>
        <h1 className="font-display font-black text-3xl text-center text-[var(--miro-navy)] mt-1">
          You're all set
        </h1>
        <p className="text-sm text-[var(--miro-text-secondary)] mt-4 text-center">
          Your online payment for <strong>{data.reference}</strong> has been verified automatically.
          {data.auto_approved
            ? " Your account is now active — sign in with the credentials from your welcome email."
            : " Your application is now under review — we'll notify you by email once your account is activated."}
        </p>
        <div className="mt-5 rounded-lg border border-[var(--miro-border)] bg-[var(--miro-bg)] p-4 text-sm text-[var(--miro-text-primary)]">
          <div className="flex justify-between"><span>Payment ID</span><strong className="font-mono text-xs">{data.payment_id}</strong></div>
          <div className="flex justify-between"><span>Application status</span>
            <strong className={data.auto_approved ? "text-emerald-700" : ""}>
              {data.auto_approved ? "Active" : "Under review"}
            </strong>
          </div>
          {data.partner_code && (
            <div className="flex justify-between"><span>Your partner code</span>
              <strong className="font-mono text-xs">{data.partner_code}</strong>
            </div>
          )}
        </div>
        <Button asChild className="w-full mt-6 h-11 font-semibold" style={{ background: "var(--miro-navy)" }}>
          <Link to="/login">Back to sign in</Link>
        </Button>
      </div>
    </div>
  );
}
